import { QueryFunctionContext } from '@tanstack/react-query';
import { compact, flatMap, isEmpty, keyBy, map, some, trim, uniq } from 'es-toolkit/compat';
import { useMemo } from 'react';
import { JsonParam, useQueryParam } from 'use-query-params';

import { getJobs, GetJobsParams, JobFilters } from 'api/jobs';
import { ALL_STUDIES_VALUE } from 'components/SearchFilters/hooks/useMainFilters';
import { Job, JobArchiveState, JobResponse } from 'interfaces/job';
import { useAppSelector } from 'redux/hooks';
import { getBatches } from 'utils/batching';
import { canQueryUsersEndpoint } from 'utils/canQueryUsersEndpoint';
import { mapUiJobsFiltersToApiFilters } from 'utils/jobTypeFilters';
import useUsersById from 'utils/queryHooks/useUsersById';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { usePermissions } from 'utils/usePermissions';
import {
  useQueriesWithErrorAndRetrySnackbar,
  useQueryWithErrorAndRetrySnackbar,
} from 'utils/useQueryWithErrorAndRetrySnackbar';

export const defaultRowsPerPage = 10;
export const jobsQueryKey = 'jobs';
export const getJobsQueryKey = (jobParams: GetJobsParams) => [jobsQueryKey, jobParams] as const;

// Keeps the GET /jobs query string (which is JSON-encoded into the URL) at a safe length
// while still batching many orchestration IDs into a small, constant number of requests.
export const ORCHESTRATION_IDS_PER_JOBS_REQUEST = 25;

export const useJobsQuery = <JobType extends Job = Job>(
  jobParams: GetJobsParams,
  options?: Parameters<typeof useQueryWithErrorAndRetrySnackbar<JobResponse<JobType>>>[2]
) => {
  return useQueryWithErrorAndRetrySnackbar<JobResponse<JobType>>(
    getJobsQueryKey(jobParams),
    ({ signal }): Promise<JobResponse<JobType>> => getJobs<JobType>(jobParams, signal),
    options
  );
};

/**
 * Fetches jobs for many orchestration IDs at once instead of issuing one request per orchestration ID.
 *
 * The orchestration IDs are deduped and split into a small number of fixed-size batches, each
 * fetched in parallel via a single `/jobs` request (`orchestrationIds` filter accepts an array).
 * Returns a lookup map from orchestrationId to its matching job for O(1) consumption by callers
 * that previously ran one query per row.
 */
export const useJobsByOrchestrationIds = ({
  studyId,
  orchestrationIds,
  enabled = true,
}: {
  studyId: string;
  orchestrationIds: string[];
  enabled?: boolean;
}) => {
  const { labId } = useCurrentLabId();

  const uniqueOrchestrationIds = useMemo(() => uniq(compact(orchestrationIds)), [orchestrationIds]);
  const orchestrationIdBatches = useMemo(
    () => getBatches(uniqueOrchestrationIds, ORCHESTRATION_IDS_PER_JOBS_REQUEST),
    [uniqueOrchestrationIds]
  );

  const canQueryJobs = enabled && Boolean(studyId) && Boolean(labId);

  const jobBatchQueries = useQueriesWithErrorAndRetrySnackbar<JobResponse>({
    queries: map(orchestrationIdBatches, (orchestrationIdBatch) => {
      const jobParams: GetJobsParams = {
        filters: {
          studyId,
          orchestrationIds: orchestrationIdBatch,
          archiveState: JobArchiveState.Active,
        } as JobFilters,
        labId,
        pageSize: orchestrationIdBatch.length,
      };
      return {
        queryKey: getJobsQueryKey(jobParams),
        queryFn: ({ signal }: QueryFunctionContext) => getJobs(jobParams, signal),
        enabled: canQueryJobs,
        queriedEntityName: 'job',
      };
    }),
  });

  const jobsByOrchestrationId = useMemo(
    () =>
      keyBy(
        flatMap(jobBatchQueries, (query) => query.data?.jobs ?? []),
        'orchestrationId'
      ),
    [jobBatchQueries]
  );

  return {
    jobsByOrchestrationId,
    isLoading: canQueryJobs && !isEmpty(orchestrationIdBatches) && some(jobBatchQueries, (query) => query.isLoading),
    isFetching: some(jobBatchQueries, (query) => query.isFetching),
  };
};

export const useJobs = <JobType extends Job = Job>({
  debug = false,
  enabled = true,
  page = 1,
  pageSize = defaultRowsPerPage,
  additionalFilters = {},
  refetchInterval = 60000,
  fetchWithInterval = false,
  studyId: studyIdFromProps,
  keepPreviousData = true,
  noInitialData = false,
  ...getJobsParams
}: Omit<GetJobsParams, 'filters' | 'labId'> & {
  enabled?: boolean;
  debug?: boolean;
  additionalFilters?: Partial<JobFilters>;
  refetchInterval?: number;
  fetchWithInterval?: boolean;
  studyId?: string;
  noInitialData?: boolean;
  keepPreviousData?: boolean;
}) => {
  const { labId } = useCurrentLabId();
  const currentUserId = useAppSelector((state) => state.auth.profile?.userId);
  const { hasPermission, isLoading: isLoadingPermissions } = usePermissions();
  const canQueryUsers = canQueryUsersEndpoint(hasPermission);
  const [filters] = useQueryParam('jobsFilters', JsonParam);
  const { queryParams } = useEncodedFilters();
  const studyId = studyIdFromProps || queryParams.filters?.studyId;

  const usersFilter = canQueryUsers ? filters?.users : [];
  const hasUsersFilter = !isEmpty(usersFilter);
  const { usersById, isLoadingUsers } = useUsersById({
    enabled: enabled && canQueryUsers && hasUsersFilter,
  });
  const users: JobFilters['users'] = useMemo(() => {
    return map(usersFilter, (userId) => ({ id: userId, email: usersById[userId]?.primaryEmail }));
  }, [usersFilter, usersById]);

  const filtersWithEmailUsers = { ...filters, users };

  if (!isEmpty(filtersWithEmailUsers.jobIds)) {
    filtersWithEmailUsers.jobIds = map(filtersWithEmailUsers.jobIds, trim);
  }
  const mappedUiFilters = mapUiJobsFiltersToApiFilters(filtersWithEmailUsers);

  const activeUserFilter = !canQueryUsers && currentUserId ? { users: [{ id: currentUserId }] } : {};

  const jobParams = {
    filters: {
      ...mappedUiFilters,
      archiveState: JobArchiveState.Active,
      ...additionalFilters,
      ...activeUserFilter,
      studyId: studyId === ALL_STUDIES_VALUE ? undefined : studyId,
    } as JobFilters,
    labId,
    page,
    pageSize,
    ...getJobsParams,
  };

  const canQueryJobs =
    enabled &&
    Boolean(labId) &&
    !isLoadingPermissions &&
    Boolean(canQueryUsers || currentUserId) &&
    !(hasUsersFilter && isLoadingUsers);
  const jobsQuery = useJobsQuery<JobType>(jobParams, {
    enabled: canQueryJobs,
    queriedEntityName: 'job',
    keepPreviousData,
    ...(!noInitialData && { initialData: { jobs: [], totalJobs: 0 }, initialDataUpdatedAt: 0 }),
    ...(fetchWithInterval ? { refetchInterval, staleTime: refetchInterval } : {}),
  });

  if (debug) {
    console.debug({
      canQueryJobs,
      jobParams,
      data: jobsQuery?.data,
      isLoading: jobsQuery?.isLoading,
    });
  }

  return { ...jobsQuery, isLoading: jobsQuery.isLoading && canQueryJobs, jobParams };
};
